/** Per-block structured form для Pages CMS.
 *
 * Для каждого block_type показывает свой набор полей. Эмитит изменения
 * через onChange (debounced save в родителе). Никаких raw JSON textarea.
 *
 * Schemas синхронизированы с рендерерами в apps/web/src/components/cms/blocks/.
 *
 * Picker'ы:
 *  - image_strip → CmsImageUploader (file upload → /pages/media/upload → URL)
 *  - category_grid → MultiCategoryPicker (chips + cmdk dropdown)
 *  - product_carousel → PrimaryCategoryPicker (reused из products)
 */

import { Command } from "cmdk";
import { Check, ChevronsUpDown, Loader2, Plus, Trash2, Upload, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { PrimaryCategoryPicker } from "@/components/products/CategoryPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

type Data = Record<string, unknown>;

interface Props {
  blockType: string;
  data: Data;
  onChange: (next: Data) => void;
}

export function BlockDataForm({ blockType, data, onChange }: Props): JSX.Element {
  switch (blockType) {
    case "hero":
      return <HeroForm data={data} onChange={onChange} />;
    case "text":
      return <TextForm data={data} onChange={onChange} />;
    case "image_strip":
      return <ImageStripForm data={data} onChange={onChange} />;
    case "product_carousel":
      return <ProductCarouselForm data={data} onChange={onChange} />;
    case "category_grid":
      return <CategoryGridForm data={data} onChange={onChange} />;
    case "faq":
      return <FaqForm data={data} onChange={onChange} />;
    case "html":
      return <HtmlForm data={data} onChange={onChange} />;
    case "cta":
      return <CtaForm data={data} onChange={onChange} />;
    case "banner_carousel":
      return <BannerCarouselForm data={data} onChange={onChange} />;
    case "features_grid":
      return <FeaturesGridForm data={data} onChange={onChange} />;
    case "brand_strip":
      return <BrandStripForm data={data} onChange={onChange} />;
    case "promo_cards":
      return <PromoCardsForm data={data} onChange={onChange} />;
    default:
      return (
        <p className="text-xs text-muted-foreground">
          Unknown block type: {blockType}
        </p>
      );
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────

function field<T extends Data>(data: T, onChange: (next: T) => void) {
  return <K extends keyof T>(key: K, value: T[K]) => {
    const next = { ...data, [key]: value } as T;
    onChange(next);
  };
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

function Group({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

// ─── CMS image uploader (POST /pages/media/upload) ─────────────────────

function CmsImageUploader({
  value,
  onChange,
  onRemove,
}: {
  value?: string;
  onChange: (url: string) => void;
  onRemove?: () => void;
}) {
  const [busy, setBusy] = useState(false);

  const onDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`${file.name}: > 10 MB`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post<{ url: string }>(
        "/pages/media/upload",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onChange(data.url);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? "Не удалось загрузить");
    } finally {
      setBusy(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp"] },
    maxFiles: 1,
    multiple: false,
  });

  if (value) {
    return (
      <div className="relative group border rounded overflow-hidden">
        <img
          src={value}
          alt=""
          className="w-full h-32 object-cover bg-muted"
        />
        <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-2 py-1 truncate">
          {value}
        </div>
        {onRemove && (
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition"
            onClick={onRemove}
            type="button"
          >
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={cn(
        "border-2 border-dashed rounded p-3 text-center cursor-pointer transition-colors text-xs",
        isDragActive
          ? "border-primary bg-primary/5"
          : "border-muted-foreground/30 hover:border-muted-foreground/50",
      )}
    >
      <input {...getInputProps()} />
      {busy ? (
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Загрузка…
        </div>
      ) : (
        <div className="flex items-center justify-center gap-1.5 text-muted-foreground">
          <Upload className="w-3.5 h-3.5" />
          {isDragActive ? "Отпусти файл" : "Загрузить картинку"}
        </div>
      )}
    </div>
  );
}

// ─── Multi-category picker (chips + cmdk dropdown) ─────────────────────

interface AdminCategoryRaw {
  id: number;
  parent_id: number | null;
  slug: string;
  translations: Array<{ language: string; name: string }>;
}

interface FlatCat {
  id: number;
  name: string;
  depth: number;
  path: string;
}

function flattenCategories(rows: AdminCategoryRaw[]): FlatCat[] {
  const nameOf = (c: AdminCategoryRaw): string => {
    const ru = c.translations.find((t) => t.language === "ru")?.name;
    return ru ?? c.translations[0]?.name ?? c.slug;
  };
  const byParent = new Map<number | null, AdminCategoryRaw[]>();
  for (const c of rows) {
    const arr = byParent.get(c.parent_id) ?? [];
    arr.push(c);
    byParent.set(c.parent_id, arr);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => nameOf(a).localeCompare(nameOf(b), "ru"));
  }
  const out: FlatCat[] = [];
  const walk = (parent: number | null, depth: number, parentPath: string) => {
    const kids = byParent.get(parent) ?? [];
    for (const c of kids) {
      const name = nameOf(c);
      const path = parentPath ? `${parentPath} › ${name}` : name;
      out.push({ id: c.id, name, depth, path });
      walk(c.id, depth + 1, path);
    }
  };
  walk(null, 0, "");
  return out;
}

function MultiCategoryPicker({
  value,
  onChange,
}: {
  value: number[];
  onChange: (ids: number[]) => void;
}) {
  const { data: cats, isLoading } = useQuery({
    queryKey: ["admin-categories-flat-cms"],
    queryFn: async () => {
      const { data } = await api.get<AdminCategoryRaw[]>("/categories");
      return flattenCategories(data);
    },
    staleTime: 5 * 60 * 1000,
  });

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const flat = cats ?? [];
  const selectedSet = new Set(value);
  const selectedItems = flat.filter((c) => selectedSet.has(c.id));

  const toggle = (id: number) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((x) => x !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {selectedItems.length === 0 ? (
          <span className="text-xs text-muted-foreground">
            Не выбрано (грид покажет все категории)
          </span>
        ) : (
          selectedItems.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs"
              title={c.path}
            >
              {c.name}
              <button
                type="button"
                onClick={() => toggle(c.id)}
                className="rounded-full transition hover:bg-background"
                aria-label={`Убрать ${c.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))
        )}
      </div>
      <div className="relative inline-block" ref={ref}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen((o) => !o)}
          disabled={isLoading}
        >
          <ChevronsUpDown className="w-3 h-3 mr-1" />
          {value.length ? "Добавить ещё" : "Выбрать категории"}
        </Button>
        {open && (
          <div className="absolute left-0 top-full z-50 mt-1 w-80 rounded-md border border-border bg-popover shadow-md">
            <Command shouldFilter>
              <Command.Input
                autoFocus
                placeholder="Поиск категории…"
                className="h-9 w-full border-b border-border bg-transparent px-3 text-sm outline-none placeholder:text-muted-foreground"
              />
              <Command.List className="max-h-72 overflow-auto p-1">
                <Command.Empty className="px-3 py-3 text-sm text-muted-foreground text-center">
                  Ничего не найдено
                </Command.Empty>
                {flat.map((c) => {
                  const checked = selectedSet.has(c.id);
                  return (
                    <Command.Item
                      key={c.id}
                      value={c.path}
                      onSelect={() => toggle(c.id)}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent aria-selected:bg-accent"
                    >
                      <Check
                        className={cn(
                          "h-4 w-4",
                          checked ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span style={{ paddingLeft: c.depth * 12 }}>{c.name}</span>
                    </Command.Item>
                  );
                })}
              </Command.List>
            </Command>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Hero ──────────────────────────────────────────────────────────────

function HeroForm({ data, onChange }: { data: Data; onChange: (n: Data) => void }) {
  const set = field(data, onChange);
  return (
    <div className="space-y-2">
      <Row>
        <Group label="Badge">
          <Input
            value={(data.badge as string) ?? ""}
            onChange={(e) => set("badge", e.target.value)}
            placeholder="Best Digital"
          />
        </Group>
        <Group label="Title *">
          <Input
            value={(data.title as string) ?? ""}
            onChange={(e) => set("title", e.target.value)}
          />
        </Group>
      </Row>
      <Group label="Subtitle">
        <Textarea
          rows={2}
          value={(data.subtitle as string) ?? ""}
          onChange={(e) => set("subtitle", e.target.value)}
        />
      </Group>
      <Row>
        <Group label="CTA text">
          <Input
            value={(data.cta_text as string) ?? ""}
            onChange={(e) => set("cta_text", e.target.value)}
            placeholder="В каталог"
          />
        </Group>
        <Group label="CTA path">
          <Input
            value={(data.cta_path as string) ?? ""}
            onChange={(e) => set("cta_path", e.target.value)}
            placeholder="/catalog"
          />
        </Group>
      </Row>
    </div>
  );
}

// ─── Text ──────────────────────────────────────────────────────────────

function TextForm({ data, onChange }: { data: Data; onChange: (n: Data) => void }) {
  const set = field(data, onChange);
  return (
    <div className="space-y-2">
      <Row>
        <Group label="Heading">
          <Input
            value={(data.heading as string) ?? ""}
            onChange={(e) => set("heading", e.target.value)}
          />
        </Group>
        <Group label="Align">
          <Select
            value={(data.align as string) || "left"}
            onValueChange={(v) => set("align", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="left">left</SelectItem>
              <SelectItem value="center">center</SelectItem>
            </SelectContent>
          </Select>
        </Group>
      </Row>
      <Group label="Body (markdown/html)">
        <Textarea
          rows={5}
          value={(data.body as string) ?? ""}
          onChange={(e) => set("body", e.target.value)}
        />
      </Group>
    </div>
  );
}

// ─── Image Strip ───────────────────────────────────────────────────────

interface StripImage {
  url?: string;
  alt?: string;
  caption?: string;
}

function ImageStripForm({
  data,
  onChange,
}: {
  data: Data;
  onChange: (n: Data) => void;
}) {
  const set = field(data, onChange);
  const images = (data.images as StripImage[]) || [];

  const updateImage = (idx: number, patch: Partial<StripImage>) => {
    const next = images.map((img, i) => (i === idx ? { ...img, ...patch } : img));
    set("images", next);
  };
  const removeImage = (idx: number) => {
    set("images", images.filter((_, i) => i !== idx));
  };
  const addImage = () => {
    set("images", [...images, { url: "", alt: "" }]);
  };

  return (
    <div className="space-y-2">
      <Row>
        <Group label="Heading">
          <Input
            value={(data.heading as string) ?? ""}
            onChange={(e) => set("heading", e.target.value)}
          />
        </Group>
        <Group label="Columns">
          <Input
            type="number"
            min={1}
            max={6}
            value={(data.columns as number) ?? ""}
            onChange={(e) =>
              set("columns", e.target.value ? Number(e.target.value) : undefined)
            }
          />
        </Group>
      </Row>
      <div className="space-y-2">
        <Label className="text-xs">Изображения</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {images.map((img, idx) => (
            <div
              key={idx}
              className="border rounded p-2 space-y-1.5 bg-background"
            >
              <CmsImageUploader
                value={img.url || undefined}
                onChange={(url) => updateImage(idx, { url })}
                onRemove={() => updateImage(idx, { url: "" })}
              />
              <Input
                placeholder="alt"
                className="h-7 text-xs"
                value={img.alt ?? ""}
                onChange={(e) => updateImage(idx, { alt: e.target.value })}
              />
              <Input
                placeholder="caption"
                className="h-7 text-xs"
                value={img.caption ?? ""}
                onChange={(e) => updateImage(idx, { caption: e.target.value })}
              />
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-6 text-xs"
                onClick={() => removeImage(idx)}
              >
                <Trash2 className="w-3 h-3 mr-1 text-destructive" />
                Удалить
              </Button>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={addImage}>
          <Plus className="w-3 h-3 mr-1" />
          Добавить картинку
        </Button>
      </div>
    </div>
  );
}

// ─── Product Carousel ──────────────────────────────────────────────────

function ProductCarouselForm({
  data,
  onChange,
}: {
  data: Data;
  onChange: (n: Data) => void;
}) {
  const set = field(data, onChange);
  const categoryId = (data.category_id as number) ?? null;
  return (
    <div className="space-y-2">
      <Group label="Heading">
        <Input
          value={(data.heading as string) ?? ""}
          onChange={(e) => set("heading", e.target.value)}
        />
      </Group>
      <Group label="Категория (пусто = все товары)">
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <PrimaryCategoryPicker
              value={categoryId}
              onChange={(id) => set("category_id", id)}
            />
          </div>
          {categoryId != null && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => set("category_id", undefined)}
              title="Сбросить категорию"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </Group>
      <Row>
        <Group label="Limit (max 20)">
          <Input
            type="number"
            min={1}
            max={20}
            value={(data.limit as number) ?? 12}
            onChange={(e) => set("limit", Number(e.target.value))}
          />
        </Group>
        <Group label="Sort">
          <Select
            value={(data.sort as string) || "popular"}
            onValueChange={(v) => set("sort", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="popular">popular</SelectItem>
              <SelectItem value="new">new</SelectItem>
              <SelectItem value="price_asc">price ↑</SelectItem>
              <SelectItem value="price_desc">price ↓</SelectItem>
              <SelectItem value="rating">rating</SelectItem>
            </SelectContent>
          </Select>
        </Group>
      </Row>
      <Group label="Only on sale">
        <Select
          value={data.on_sale ? "true" : "false"}
          onValueChange={(v) => set("on_sale", v === "true")}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="false">все товары</SelectItem>
            <SelectItem value="true">только sale</SelectItem>
          </SelectContent>
        </Select>
      </Group>
    </div>
  );
}

// ─── Category Grid ─────────────────────────────────────────────────────

function CategoryGridForm({
  data,
  onChange,
}: {
  data: Data;
  onChange: (n: Data) => void;
}) {
  const set = field(data, onChange);
  const ids = (data.category_ids as number[]) || [];
  return (
    <div className="space-y-2">
      <Row>
        <Group label="Heading">
          <Input
            value={(data.heading as string) ?? ""}
            onChange={(e) => set("heading", e.target.value)}
          />
        </Group>
        <Group label="Columns (max 6)">
          <Input
            type="number"
            min={1}
            max={6}
            value={(data.columns as number) ?? 4}
            onChange={(e) => set("columns", Number(e.target.value))}
          />
        </Group>
      </Row>
      <Group label="Категории">
        <MultiCategoryPicker
          value={ids}
          onChange={(next) => set("category_ids", next)}
        />
      </Group>
    </div>
  );
}

// ─── FAQ ───────────────────────────────────────────────────────────────

interface FaqItem {
  q?: string;
  a?: string;
}

function FaqForm({ data, onChange }: { data: Data; onChange: (n: Data) => void }) {
  const set = field(data, onChange);
  const items = (data.items as FaqItem[]) || [];

  const update = (idx: number, patch: Partial<FaqItem>) => {
    set(
      "items",
      items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    );
  };
  const remove = (idx: number) =>
    set("items", items.filter((_, i) => i !== idx));
  const add = () => set("items", [...items, { q: "", a: "" }]);

  return (
    <div className="space-y-2">
      <Group label="Heading">
        <Input
          value={(data.heading as string) ?? ""}
          onChange={(e) => set("heading", e.target.value)}
        />
      </Group>
      <div className="space-y-2">
        <Label className="text-xs">Вопросы / ответы</Label>
        {items.map((it, idx) => (
          <div key={idx} className="border rounded p-2 space-y-1.5 bg-background">
            <div className="flex items-center gap-2">
              <Input
                placeholder="Вопрос"
                value={it.q ?? ""}
                onChange={(e) => update(idx, { q: e.target.value })}
              />
              <Button variant="ghost" size="icon" onClick={() => remove(idx)}>
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
              </Button>
            </div>
            <Textarea
              placeholder="Ответ"
              rows={2}
              value={it.a ?? ""}
              onChange={(e) => update(idx, { a: e.target.value })}
            />
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={add}>
          <Plus className="w-3 h-3 mr-1" />
          Добавить пункт
        </Button>
      </div>
    </div>
  );
}

// ─── HTML ──────────────────────────────────────────────────────────────

function HtmlForm({ data, onChange }: { data: Data; onChange: (n: Data) => void }) {
  const set = field(data, onChange);
  return (
    <Group label="Raw HTML (осторожно — XSS не санитайзится)">
      <Textarea
        rows={6}
        className="font-mono text-xs"
        value={(data.html as string) ?? ""}
        onChange={(e) => set("html", e.target.value)}
      />
    </Group>
  );
}

// ─── CTA ───────────────────────────────────────────────────────────────

function CtaForm({ data, onChange }: { data: Data; onChange: (n: Data) => void }) {
  const set = field(data, onChange);
  return (
    <div className="space-y-2">
      <Row>
        <Group label="Heading *">
          <Input
            value={(data.heading as string) ?? ""}
            onChange={(e) => set("heading", e.target.value)}
          />
        </Group>
        <Group label="Variant">
          <Select
            value={(data.variant as string) || "navy"}
            onValueChange={(v) => set("variant", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="navy">navy</SelectItem>
              <SelectItem value="light">light</SelectItem>
            </SelectContent>
          </Select>
        </Group>
      </Row>
      <Group label="Subtitle">
        <Textarea
          rows={2}
          value={(data.subtitle as string) ?? ""}
          onChange={(e) => set("subtitle", e.target.value)}
        />
      </Group>
      <Row>
        <Group label="Button text *">
          <Input
            value={(data.button_text as string) ?? ""}
            onChange={(e) => set("button_text", e.target.value)}
          />
        </Group>
        <Group label="Button path *">
          <Input
            value={(data.button_path as string) ?? ""}
            onChange={(e) => set("button_path", e.target.value)}
            placeholder="/catalog"
          />
        </Group>
      </Row>
    </div>
  );
}

// ─── Banner carousel ───────────────────────────────────────────────────

interface Slide {
  image_url?: string;
  title?: string;
  subtitle?: string;
  cta_text?: string;
  cta_path?: string;
  badge?: string;
  bg_color?: string;
}

function BannerCarouselForm({
  data,
  onChange,
}: {
  data: Data;
  onChange: (n: Data) => void;
}) {
  const set = field(data, onChange);
  const slides = (data.slides as Slide[]) || [];

  const updateSlide = (idx: number, patch: Partial<Slide>) => {
    set("slides", slides.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };
  const addSlide = () => set("slides", [...slides, { title: "Новый слайд" }]);
  const removeSlide = (idx: number) =>
    set("slides", slides.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      <Group label="Autoplay (мс, 0 = выкл)">
        <Input
          type="number"
          value={(data.autoplay_ms as number) ?? 5000}
          onChange={(e) => set("autoplay_ms", Number(e.target.value) || 0)}
        />
      </Group>
      <Label className="text-xs">Слайды</Label>
      <div className="space-y-2">
        {slides.map((s, idx) => (
          <div key={idx} className="border rounded p-2 space-y-1.5 bg-background">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-muted-foreground">
                Слайд #{idx + 1}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeSlide(idx)}
                className="ml-auto"
              >
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
              </Button>
            </div>
            <CmsImageUploader
              value={s.image_url || undefined}
              onChange={(image_url) => updateSlide(idx, { image_url })}
              onRemove={() => updateSlide(idx, { image_url: "" })}
            />
            <Row>
              <Input
                placeholder="Бейдж"
                value={s.badge ?? ""}
                onChange={(e) => updateSlide(idx, { badge: e.target.value })}
              />
              <Input
                placeholder="Фон (#1E73E8)"
                value={s.bg_color ?? ""}
                onChange={(e) => updateSlide(idx, { bg_color: e.target.value })}
                className="font-mono"
              />
            </Row>
            <Input
              placeholder="Заголовок"
              value={s.title ?? ""}
              onChange={(e) => updateSlide(idx, { title: e.target.value })}
            />
            <Textarea
              placeholder="Подзаголовок"
              rows={2}
              value={s.subtitle ?? ""}
              onChange={(e) => updateSlide(idx, { subtitle: e.target.value })}
            />
            <Row>
              <Input
                placeholder="CTA text"
                value={s.cta_text ?? ""}
                onChange={(e) => updateSlide(idx, { cta_text: e.target.value })}
              />
              <Input
                placeholder="CTA path /catalog"
                value={s.cta_path ?? ""}
                onChange={(e) => updateSlide(idx, { cta_path: e.target.value })}
              />
            </Row>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addSlide}>
          <Plus className="w-3 h-3 mr-1" />
          Добавить слайд
        </Button>
      </div>
    </div>
  );
}

// ─── Features grid ─────────────────────────────────────────────────────

interface FeatureItem {
  icon?: string;
  title?: string;
  subtitle?: string;
  href?: string;
  accent?: "default" | "highlight";
}

function FeaturesGridForm({
  data,
  onChange,
}: {
  data: Data;
  onChange: (n: Data) => void;
}) {
  const set = field(data, onChange);
  const items = (data.items as FeatureItem[]) || [];

  const update = (idx: number, patch: Partial<FeatureItem>) =>
    set("items", items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx: number) =>
    set("items", items.filter((_, i) => i !== idx));
  const add = () => set("items", [...items, { title: "Новая карточка", icon: "sparkles" }]);

  return (
    <div className="space-y-2">
      <Group label="Колонок (max 6)">
        <Input
          type="number"
          min={1}
          max={6}
          value={(data.columns as number) ?? 4}
          onChange={(e) => set("columns", Number(e.target.value))}
        />
      </Group>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <div key={idx} className="border rounded p-2 space-y-1.5 bg-background">
            <Row>
              <Input
                placeholder="Заголовок"
                value={it.title ?? ""}
                onChange={(e) => update(idx, { title: e.target.value })}
              />
              <Input
                placeholder="Иконка (percent/sparkles/badge/truck)"
                value={it.icon ?? ""}
                onChange={(e) => update(idx, { icon: e.target.value })}
                className="font-mono"
              />
            </Row>
            <Input
              placeholder="Подзаголовок"
              value={it.subtitle ?? ""}
              onChange={(e) => update(idx, { subtitle: e.target.value })}
            />
            <Row>
              <Input
                placeholder="Ссылка /promotions"
                value={it.href ?? ""}
                onChange={(e) => update(idx, { href: e.target.value })}
              />
              <Select
                value={it.accent || "default"}
                onValueChange={(v: "default" | "highlight") =>
                  update(idx, { accent: v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">обычный</SelectItem>
                  <SelectItem value="highlight">подсветка (зелёный)</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Button variant="ghost" size="sm" className="w-full h-6 text-xs" onClick={() => remove(idx)}>
              <Trash2 className="w-3 h-3 mr-1 text-destructive" />
              Удалить
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={add}>
          <Plus className="w-3 h-3 mr-1" />
          Добавить карточку
        </Button>
      </div>
    </div>
  );
}

// ─── Brand strip ───────────────────────────────────────────────────────

function BrandStripForm({
  data,
  onChange,
}: {
  data: Data;
  onChange: (n: Data) => void;
}) {
  const set = field(data, onChange);
  const ids = ((data.brand_ids as number[]) || []).join(",");
  return (
    <div className="space-y-2">
      <Row>
        <Group label="Заголовок">
          <Input
            value={(data.heading as string) ?? ""}
            onChange={(e) => set("heading", e.target.value)}
            placeholder="Более 15 000 проверенных брендов"
          />
        </Group>
        <Group label="Subheading (link to /brands)">
          <Input
            value={(data.subheading as string) ?? ""}
            onChange={(e) => set("subheading", e.target.value)}
            placeholder="Смотреть все"
          />
        </Group>
      </Row>
      <Group label="Лимит брендов">
        <Input
          type="number"
          min={1}
          max={30}
          value={(data.limit as number) ?? 12}
          onChange={(e) => set("limit", Number(e.target.value))}
        />
      </Group>
      <Group label="Brand IDs (через запятую, пусто = все active)">
        <Input
          value={ids}
          onChange={(e) => {
            const parsed = e.target.value
              .split(",")
              .map((s) => Number(s.trim()))
              .filter((n) => Number.isFinite(n) && n > 0);
            set("brand_ids", parsed);
          }}
          placeholder="3,7,12"
        />
      </Group>
      <p className="text-[10px] text-muted-foreground">
        Создавать бренды → Configuration → Бренды
      </p>
    </div>
  );
}

// ─── Promo cards ───────────────────────────────────────────────────────

interface PromoCard {
  image_url?: string;
  title?: string;
  deadline?: string;
  href?: string;
}

function PromoCardsForm({
  data,
  onChange,
}: {
  data: Data;
  onChange: (n: Data) => void;
}) {
  const set = field(data, onChange);
  const cards = (data.cards as PromoCard[]) || [];

  const update = (idx: number, patch: Partial<PromoCard>) =>
    set("cards", cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const remove = (idx: number) =>
    set("cards", cards.filter((_, i) => i !== idx));
  const add = () => set("cards", [...cards, { title: "Новая акция" }]);

  return (
    <div className="space-y-2">
      <Row>
        <Group label="Заголовок">
          <Input
            value={(data.heading as string) ?? ""}
            onChange={(e) => set("heading", e.target.value)}
            placeholder="Наши акции"
          />
        </Group>
        <Group label="See-all href">
          <Input
            value={(data.see_all_href as string) ?? ""}
            onChange={(e) => set("see_all_href", e.target.value)}
            placeholder="/promotions"
          />
        </Group>
      </Row>
      <Label className="text-xs">Карточки</Label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {cards.map((c, idx) => (
          <div key={idx} className="border rounded p-2 space-y-1.5 bg-background">
            <CmsImageUploader
              value={c.image_url || undefined}
              onChange={(image_url) => update(idx, { image_url })}
              onRemove={() => update(idx, { image_url: "" })}
            />
            <Input
              placeholder="Заголовок"
              value={c.title ?? ""}
              onChange={(e) => update(idx, { title: e.target.value })}
              className="h-7 text-xs"
            />
            <Input
              placeholder="Дедлайн (До 18 июня)"
              value={c.deadline ?? ""}
              onChange={(e) => update(idx, { deadline: e.target.value })}
              className="h-7 text-xs"
            />
            <Input
              placeholder="Ссылка /promotions/xxx"
              value={c.href ?? ""}
              onChange={(e) => update(idx, { href: e.target.value })}
              className="h-7 text-xs"
            />
            <Button variant="ghost" size="sm" className="w-full h-6 text-xs" onClick={() => remove(idx)}>
              <Trash2 className="w-3 h-3 mr-1 text-destructive" />
              Удалить
            </Button>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={add}>
        <Plus className="w-3 h-3 mr-1" />
        Добавить карточку
      </Button>
    </div>
  );
}
